<script setup lang="ts">
import {
  HgPaginationSelector,
  HgSupportedLanguage,
  SupportedPlatforms,
} from '@smg-real-estate/domus';
import type { Locale } from '@smg-real-estate/geo-libs';
import { FeaturedPlacement } from '@smg-real-estate/seeker-app-shared-components';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { DEFAULT_AGENCY_PAGE_LIST_SIZE } from '@/api/customerMetaApi';
import AgentCard from '@/components/AgentCard/AgentCard.vue';
import AgentCardPlaceHolder from '@/components/AgentCard/AgentCardPlaceHolder.vue';
import ParentLocationAgentSuggestions from '@/components/ParentLocationAgentSuggestions/ParentLocationAgentSuggestions.vue';
import ResultsHeaderPlaceholder from '@/components/ResultsHeaderPlaceholder/ResultsHeaderPlaceholder.vue';
import ZeroSearchResult from '@/components/ZeroSearchResult/ZeroSearchResult.vue';
import { buildFacetQuery } from '@/helpers/AgentDirectory/agentFacetRouteParams';
import { getDistinguishedDisplayValue } from '@/helpers/Geo/GeoLocationHelper';
import { isPlatformHg, PlatformEnvs } from '@/helpers/Platform/platform';
import { getAgentBasePathWithLocation } from '@/router';
import { useLocale } from '@/router/routeMeta';
import { useAgentProfilesStore } from '@/stores/agentProfiles';
import { trackPaginationClick, trackSearchResultsAgentCardClick } from '@/tracking/tracking';
import type { AgentProfile } from '@/types/agentProfile';

const agentProfilesStore = useAgentProfilesStore();
const {
  searchedGeoIds,
  isSearchedLocationsStale,
  parentSuggestionAgents,
  parentSuggestionLocation,
  searchedFacets,
} = storeToRefs(agentProfilesStore);

const platform = isPlatformHg() ? SupportedPlatforms.hg : SupportedPlatforms.is24;
const isProduction = import.meta.env.VUE_APP_ENVIRONMENT === PlatformEnvs.prod;

const locale = useLocale();

const { t } = useI18n();

const props = defineProps<{
  loadingAgents: boolean;
  agentsLoadFailed: boolean;
  agents: AgentProfile[];
  totalAgentsCount: number;
  agentListCurrentPage: number;
  pageCount: number;
  language: HgSupportedLanguage;
  locationsForSeo: string;
}>();

const pageRoute = (page: number) => {
  return agentProfilesStore.nextPageRoute(page, locale.value as unknown as Locale);
};

const isSuccessfulEmpty = computed(() => !props.loadingAgents && props.totalAgentsCount === 0);

const searchResultsHeading = computed(() =>
  isSuccessfulEmpty.value
    ? t('agentList.searchResultsEmpty', {
        location: props.locationsForSeo,
      })
    : t('agentList.searchResults', {
        numberOfResult: props.totalAgentsCount,
        location: props.locationsForSeo,
      }),
);

const translations = computed(() => ({
  featuredAgenciesSectionTitle: '',
  featuredAgenciesPreviewBadge: t('agencies.featuredAgenciesPreviewBadge').toString(),
  featuredAgenciesDescriptionsTitle: t('agencies.featuredAgenciesDescriptionsTitle').toString(),
  featuredAgenciesServicesTitle: t('agencies.featuredAgenciesServicesTitle').toString(),
  featuredAgenciesGenericDescription: t('agencies.featuredAgenciesGenericDescription').toString(),
  agencyCTA: t('agencies.agencyCTA').toString(),
  searchResults: t('agentList.searchResults', {
    numberOfResult: props.totalAgentsCount,
    location: props.locationsForSeo,
  }).toString(),
}));

const agentsPlaceHolderCount = computed(() => (props.loadingAgents ? 20 : 0));

const showParentSuggestions = computed(
  () =>
    !props.loadingAgents &&
    !props.agentsLoadFailed &&
    props.agents.length === 0 &&
    parentSuggestionAgents.value.length > 0 &&
    parentSuggestionLocation.value !== null,
);

const parentSuggestionLocationLabel = computed(() => {
  const location = parentSuggestionLocation.value;
  if (!location) {
    return '';
  }
  return getDistinguishedDisplayValue(location, props.language);
});

const parentSuggestionSeeAllRoute = computed(() => {
  const location = parentSuggestionLocation.value;
  if (!location) {
    return undefined;
  }

  const urlName = location.urlNames[props.language] ?? location.urlNames[HgSupportedLanguage.DE];

  return {
    path: getAgentBasePathWithLocation(props.language, urlName),
    query: buildFacetQuery(searchedFacets.value),
  };
});

const handleAgentCardClick = (clickedAgent: AgentProfile, index: number) => {
  trackSearchResultsAgentCardClick({
    clickedAgent,
    position:
      (agentProfilesStore.agentListCurrentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE + index + 1,
  });
};
</script>

<template>
  <div :class="$style.container">
    <ResultsHeaderPlaceholder v-if="loadingAgents" />
    <div v-else :class="$style.resultsHeader">
      <h1 :class="$style.resultsHeaderText">
        {{ searchResultsHeading }}
      </h1>
    </div>
    <div>
      <FeaturedPlacement
        v-if="searchedGeoIds && !isSearchedLocationsStale"
        data-dd-action-name="Featured Agencies"
        :currentPage="agentListCurrentPage"
        :language="language"
        :geoIds="searchedGeoIds"
        :translations="translations"
        :isProduction="isProduction"
        :platform="platform"
      >
        <template #agencyText>
          {{ $t('agencies.featuredAgenciesGenericDescription') }}
        </template>
      </FeaturedPlacement>
    </div>
    <div v-if="!loadingAgents && agentsLoadFailed">
      <ZeroSearchResult titleKey="agentList.loadFailed" subtitleKey="agentList.tryAgainLater" />
    </div>
    <div v-else-if="!loadingAgents && !agents.length">
      <ZeroSearchResult
        titleKey="agentList.noAgentsFound"
        subtitleKey="agencyList.tryAnotherLocation"
      />
      <ParentLocationAgentSuggestions
        v-if="showParentSuggestions"
        :agents="parentSuggestionAgents"
        :locationLabel="parentSuggestionLocationLabel"
        :seeAllRoute="parentSuggestionSeeAllRoute"
        :language="language"
        @agentCardClick="handleAgentCardClick"
      />
    </div>
    <div :class="$style.agentList">
      <AgentCard
        v-for="(agent, index) in agents"
        :key="agent.agentId"
        :agent="agent"
        :language="language"
        @agentCardClick="handleAgentCardClick(agent, index)"
      />
      <AgentCardPlaceHolder v-for="n in agentsPlaceHolderCount" :key="`placeholder-${n}`" />
    </div>
    <div v-if="agents.length > 0" :class="$style.agentListPagination">
      <HgPaginationSelector
        :totalPages="pageCount"
        :currentPage="agentListCurrentPage"
        :pageRoute="pageRoute"
        @paginationClicked="trackPaginationClick"
      />
    </div>
  </div>
</template>

<style module lang="scss">
@use '@/assets/variables' as *;

.container {
  display: flex;
  flex-direction: column;
  gap: $space-150;
  margin-top: $space-150;
  margin-bottom: $space-200;
}

.agentList {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-150;
}

.resultsHeader {
  display: flex;
  flex-wrap: wrap;
  gap: $space-50;
  justify-content: space-between;
}

.resultsHeaderText {
  margin: 0;

  @include heading-2;
}

.agentListPagination {
  padding-top: $space-250;
}
</style>
